import { useCallback, useEffect, useState } from 'react';
import * as duckdb from '@duckdb/duckdb-wasm';
import { isMobileBrowser } from '../utils/isMobileBrowser';

// Development mode check
const isDevelopment = process.env.NODE_ENV === 'development';

// Conditional logging helpers - only log in development
const devLog = (...args: any[]) => {
  if (isDevelopment) console.log(...args);
};

const devWarn = (...args: any[]) => {
  if (isDevelopment) console.warn(...args);
};

// Always log errors
const devError = (...args: any[]) => {
  console.error(...args);
};

// Custom DuckDB logger that respects development mode
class ConditionalLogger extends duckdb.ConsoleLogger {
  log(entry: duckdb.LogEntryVariant): void {
    if (isDevelopment) {
      super.log(entry);
    }
  }
}

const DEFAULT_S3_BUCKET = 's3://eduinsights/';
const DEFAULT_EXPORT_PREFIX = 'frontend';
const OPFS_DB_URI = 'opfs://kolada-cache.duckdb';
const FACT_MUNICIPALITY_TABLE = 'fact_kpi_trend_municipality_local';
const FACT_ORG_UNIT_TABLE = 'fact_kpi_trend_org_unit_local';
/** Exported fact columns (parquet may omit precomputed trend fields; views recompute via LAG). */
const FACT_KPI_BASE_COLUMNS =
  'entity_id, municipality_id, kpi_id, year, gender, value, count, status, unit, students, weighted_value, governance_type_year';
export const MUNICIPALITY_BOUNDARIES_TABLE = 'dim_municipality_boundaries_local';
const BOUNDARIES_TABLE = MUNICIPALITY_BOUNDARIES_TABLE;
/** Radar cache columns used by useRadarProfile (mobile loads subset to save memory). */
const RADAR_CACHE_SLIM_COLUMNS = `
  municipality_id, municipality_name, year, kpi_id, kpi_title, radar_category, display_order, higher_is_better,
  municipality_value, municipality_students,
  county_code, county_name, skr_group_id, skr_group_name,
  lan_benchmark_value, lan_benchmark_students, lan_municipality_count,
  liknade_benchmark_value, liknade_benchmark_students, liknade_municipality_count,
  riket_benchmark_value, riket_benchmark_students, riket_municipality_count,
  school_count, school_q1, school_q3, uncertainty_sd,
  percentile, rank, cohort_size, delta_from_riket,
  cohort_min, cohort_p25, cohort_median, cohort_p75, cohort_max
`.replace(/\s+/g, ' ').trim();
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
const CACHE_TIMESTAMP_KEY = 'kolada_cache_refreshed_at';
const CACHE_OPFS_DISABLED_KEY = 'kolada_cache_opfs_disabled';
// Schema version - bump this when parquet schema changes to force cache refresh
const CACHE_SCHEMA_VERSION = '13'; // v13: Pruned fact parquet + ZSTD; mobile streaming; geo/radar slim loads
devLog('🔧 useSqlRooms loaded, CACHE_SCHEMA_VERSION:', CACHE_SCHEMA_VERSION);
const CACHE_SCHEMA_VERSION_KEY = 'kolada_cache_schema_version';
const supportsFileSystemAccess =

  typeof navigator !== 'undefined' &&
  !!navigator.storage &&
  typeof (navigator.storage as any).getDirectory === 'function';

const isOpfsDisabled = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return false;
    return window.localStorage.getItem(CACHE_OPFS_DISABLED_KEY) === '1';
  } catch {
    return false;
  }
};

const setOpfsDisabled = (disabled: boolean) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    if (disabled) {
      window.localStorage.setItem(CACHE_OPFS_DISABLED_KEY, '1');
    } else {
      window.localStorage.removeItem(CACHE_OPFS_DISABLED_KEY);
    }
  } catch (error) {
    console.warn('Failed to update OPFS disabled flag.', error);
  }
};

async function getOpfsFileHandle(create: boolean) {
  if (!supportsFileSystemAccess) return null;
  try {
    const root = await (navigator.storage as any).getDirectory();
    return await root.getFileHandle(OPFS_DB_URI.replace('opfs://', ''), { create });
  } catch (error) {
    console.warn('Failed to access OPFS file handle:', error);
    return null;
  }
}

async function registerOpfsDatabase(dbInstance: duckdb.AsyncDuckDB) {
  if (!supportsFileSystemAccess) return false;
  const handle = await getOpfsFileHandle(true);
  if (!handle) return false;

  const protocol =
    (((duckdb.DuckDBDataProtocol as any)?.BROWSER_FSACCESS as number | undefined) ??
      duckdb.DuckDBDataProtocol.BROWSER_FSACCESS ??
      duckdb.DuckDBDataProtocol.BROWSER_FILEREADER);

  try {
    await dbInstance.registerFileHandle(OPFS_DB_URI, handle, protocol, true);
    devLog('Registered OPFS database handle.');
    return true;
  } catch (error) {
    devWarn('registerFileHandle failed; falling back to auto OPFS handling.', error);
    setOpfsDisabled(true);
    return false;
  }
}

type DuckConnection = duckdb.AsyncDuckDBConnection;

let globalReady = false;
let globalError: string | null = null;
let initializationPromise: Promise<void> | null = null;
let db: duckdb.AsyncDuckDB | null = null;
let conn: DuckConnection | null = null;
let globalDataVersion = 0;
let globalSecondaryReady = false;

// Version listeners to notify hooks of global state changes (e.g. background load finished)
const versionListeners = new Set<(v: number) => void>();

async function debugSql(sql: string) {
  if (!conn) throw new Error('DuckDB connection not ready');
  const result = await conn.query(sql);
  return result.toArray().map(sanitizeRow);
}

// Global query cache to persist data across component remounts
// Especially useful for mobile users and the map data
const queryCache = new Map<string, any>();
const pendingQueries = new Map<string, Promise<any>>();
const MAX_QUERY_CACHE_SIZE = isMobileBrowser() ? 30 : 200;

// Shared municipality geo cache to avoid re-parsing JSON
let globalMunicipalities: any[] | null = null;

const freezeQueryResult = (value: any): any => {
  if (value === null || value === undefined) return value;
  if (Array.isArray(value)) {
    value.forEach((row) => {
      if (row && typeof row === 'object') Object.freeze(row);
    });
    return Object.freeze(value);
  }
  if (typeof value === 'object') return Object.freeze(value);
  return value;
};

const addToQueryCache = (key: string, value: any) => {
  if (queryCache.size >= MAX_QUERY_CACHE_SIZE) {
    const firstKey = queryCache.keys().next().value;
    if (firstKey) queryCache.delete(firstKey);
  }
  queryCache.set(key, freezeQueryResult(value));
};

const invalidateQueryCaches = (reason: string) => {
  globalDataVersion += 1;
  queryCache.clear();
  pendingQueries.clear();
  // globalMunicipalities contains parsed geojson; safe to keep.
  devLog(`🧹 Cleared DuckDB query cache (v${globalDataVersion}):`, reason);

  // Notify all active hooks to re-render
  versionListeners.forEach(listener => listener(globalDataVersion));
};

/** Frees WASM/OPFS handles when leaving dashboard routes (important for iPhone memory). */
async function disposeSqlRoomsEngine(reason: string): Promise<void> {
  devLog('Disposing DuckDB engine:', reason);
  resetMunicipalityBoundariesState();
  lastNormalizedS3Base = null;
  globalReady = false;
  globalSecondaryReady = false;
  globalError = null;
  initializationPromise = null;

  if (conn) {
    try {
      await conn.close();
    } catch (e) {
      devWarn('Error closing DuckDB connection on dispose:', e);
    }
    conn = null;
  }
  if (db) {
    try {
      await db.terminate();
    } catch (e) {
      devWarn('Error terminating DuckDB on dispose:', e);
    }
    db = null;
  }
  queryCache.clear();
  pendingQueries.clear();
  globalMunicipalities = null;
}

function markDuckdbReadyForProfiling() {
  try {
    if (typeof performance !== 'undefined' && performance.mark) {
      performance.mark('duckdb-ready');
    }
  } catch {
    // ignore duplicate-mark quirks
  }
}

// Lightweight BigInt conversion without full JSON round-trip
const sanitizeRow = (row: Record<string, unknown>): Record<string, unknown> => {
  const result: Record<string, unknown> = {};
  for (const key in row) {
    const val = row[key];
    result[key] = typeof val === 'bigint' ? Number(val) : val;
  }
  return result;
};

const escapeSqlString = (value: string) => value.replace(/'/g, "''");

const normalizeBucket = (bucket?: string | null) => {
  if (!bucket) return DEFAULT_S3_BUCKET;
  const trimmed = bucket.trim();
  if (!trimmed) return DEFAULT_S3_BUCKET;
  if (trimmed.startsWith('s3://')) {
    return trimmed.replace(/\/+$/, '');
  }
  return `s3://${trimmed.replace(/^\/+/, '').replace(/\/+$/, '')}`;
};

const joinS3Path = (base: string, ...segments: string[]) => {
  let result = base.replace(/\/+$/, '');
  for (const segment of segments) {
    if (!segment) continue;
    const normalized = segment.replace(/^\/+/, '').replace(/\/+$/, '');
    if (!normalized) continue;
    result = `${result}/${normalized}`;
  }
  return result;
};

/** S3 normalized/ prefix for lazy boundary load on mobile (set during DuckDB init). */
let lastNormalizedS3Base: string | null = null;
let municipalityBoundariesLoaded = false;
let boundariesLoadingPromise: Promise<void> | null = null;

function resetMunicipalityBoundariesState() {
  municipalityBoundariesLoaded = false;
  boundariesLoadingPromise = null;
}

/**
 * Mobile: load GeoJSON boundaries from S3 into a TEMP table on first map/silhouette use.
 * Desktop: no-op (boundaries already in dim_municipality_geo).
 */
export async function ensureMunicipalityBoundariesLoaded(): Promise<void> {
  if (!isMobileBrowser()) return;
  if (!conn || !globalReady) return;
  if (municipalityBoundariesLoaded) return;
  if (boundariesLoadingPromise) {
    await boundariesLoadingPromise;
    return;
  }
  const base = lastNormalizedS3Base;
  if (!base) {
    devWarn('ensureMunicipalityBoundariesLoaded: normalized S3 base not set');
    return;
  }

  boundariesLoadingPromise = (async () => {
    const dimMunGeoPath = escapeSqlString(joinS3Path(base, 'dim_municipality_geo.parquet'));
    await conn!.query(
      `CREATE OR REPLACE TEMP TABLE ${BOUNDARIES_TABLE} AS
       SELECT municipality_id, boundary_type, boundary_json
       FROM read_parquet('${dimMunGeoPath}')
       WHERE boundary_json IS NOT NULL`
    );
    municipalityBoundariesLoaded = true;
    devLog('Municipality boundaries lazy-loaded (mobile).');
  })();
  try {
    await boundariesLoadingPromise;
  } finally {
    boundariesLoadingPromise = null;
  }
}

function sqlMuniFactBase(muniSource: string): string {
  return `(SELECT ${FACT_KPI_BASE_COLUMNS} FROM ${muniSource})`;
}

function sqlMuniFactWithTrend(muniSource: string): string {
  const base = sqlMuniFactBase(muniSource);
  return `(
    SELECT
      *,
      value - prev_year_value AS value_change,
      CASE WHEN prev_year_value IS NOT NULL AND prev_year_value <> 0
        THEN (value - prev_year_value) / prev_year_value * 100 ELSE NULL END AS value_change_pct
    FROM (
      SELECT
        *,
        LAG(value) OVER (PARTITION BY entity_id, kpi_id, COALESCE(NULLIF(gender, ''), 'T') ORDER BY year) AS prev_year_value
      FROM ${base} AS _muni_base
    ) AS _muni_lag
  )`;
}

function sqlOrgFactBase(orgSource: string): string {
  return `(SELECT ${FACT_KPI_BASE_COLUMNS} FROM ${orgSource})`;
}

function sqlOrgFactWithTrend(orgSource: string): string {
  const base = sqlOrgFactBase(orgSource);
  return `(
    SELECT
      *,
      value - prev_year_value AS value_change,
      CASE WHEN prev_year_value IS NOT NULL AND prev_year_value <> 0
        THEN (value - prev_year_value) / prev_year_value * 100 ELSE NULL END AS value_change_pct
    FROM (
      SELECT
        *,
        LAG(value) OVER (PARTITION BY entity_id, kpi_id, COALESCE(NULLIF(gender, ''), 'T') ORDER BY year) AS prev_year_value
      FROM ${base} AS _org_base
    ) AS _org_lag
  )`;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const extractBucketAndKey = (s3Uri: string) => {
  const withoutScheme = s3Uri.replace(/^s3:\/\//, '');
  const [bucket, ...rest] = withoutScheme.split('/');
  return { bucket, key: rest.join('/') };
};


async function ensureTable(connection: DuckConnection, tableName: string, selectSql: string, persist = true) {
  const createClause = persist ? 'TABLE' : 'TEMP TABLE';
  // Use CREATE OR REPLACE for atomic updates to prevent catalog errors for readers
  await connection.query(`CREATE OR REPLACE ${createClause} ${tableName} AS ${selectSql}`);
  devLog(`✔ ${tableName} initialized`);
}

async function loadDimensionTables(connection: DuckConnection, normalizedBase: string, persist = true) {
  devLog('Loading essential dimension tables from', normalizedBase);

  const dimYearPath = escapeSqlString(joinS3Path(normalizedBase, 'dim_year.parquet'));
  const dimMunicipalityPath = escapeSqlString(joinS3Path(normalizedBase, 'dim_municipality.parquet'));
  const dimSchoolPath = escapeSqlString(joinS3Path(normalizedBase, 'dim_school.parquet'));
  const dimKpiPath = escapeSqlString(joinS3Path(normalizedBase, 'dim_kpi_minimal.parquet'));
  const dimMunGeoPath = escapeSqlString(joinS3Path(normalizedBase, 'dim_municipality_geo.parquet'));
  const radarCachePath = escapeSqlString(joinS3Path(normalizedBase, 'gold_radar_profile_cache.parquet'));

  // Define essential loading tasks - only what's needed for the first paint
  const tasks = [
    ensureTable(
      connection,
      'dim_year',
      `SELECT CAST(year AS INTEGER) AS year FROM read_parquet('${dimYearPath}')`,
      persist
    ),
    ensureTable(
      connection,
      'dim_municipality',
      `SELECT * FROM read_parquet('${dimMunicipalityPath}')`,
      persist
    ),
    ensureTable(
      connection,
      'dim_school',
      `SELECT 
         CAST(school_id AS VARCHAR) AS school_id, 
         school_name, 
         CAST(municipality_id AS VARCHAR) AS municipality_id,
         latitude,
         longitude
       FROM read_parquet('${dimSchoolPath}')`,
      persist
    ).catch(async (e) => {
      devWarn('dim_school geo columns not available, using fallback schema:', e);
      await ensureTable(
        connection,
        'dim_school',
        `SELECT 
           CAST(school_id AS VARCHAR) AS school_id, 
           school_name, 
           CAST(municipality_id AS VARCHAR) AS municipality_id,
           NULL::DOUBLE AS latitude,
           NULL::DOUBLE AS longitude
         FROM read_parquet('${dimSchoolPath}')`,
        persist
      );
    }),
    ensureTable(
      connection,
      'dim_kpi',
      `SELECT CAST(kpi_id AS VARCHAR) AS kpi_id, kpi_title, is_divided_by_gender FROM read_parquet('${dimKpiPath}')`,
      persist
    ).catch(() => { }), // KPI table might be missing in some exports

    // 4. Municipality Geo (mobile: centroids/bbox only — boundaries lazy-loaded from S3)
    ensureTable(
      connection,
      'dim_municipality_geo',
      isMobileBrowser()
        ? `SELECT municipality_id, municipality_name, centroid_latitude, centroid_longitude, bbox_south, bbox_north, bbox_west, bbox_east, school_count, centroid_source FROM read_parquet('${dimMunGeoPath}')`
        : `SELECT * FROM read_parquet('${dimMunGeoPath}')`,
      persist
    ).catch(e => devWarn('dim_municipality_geo not available:', e)),

    // 5. Radar Profile Cache (mobile: column subset)
    ensureTable(
      connection,
      'gold_radar_profile_cache',
      isMobileBrowser()
        ? `SELECT ${RADAR_CACHE_SLIM_COLUMNS} FROM read_parquet('${radarCachePath}')`
        : `SELECT * FROM read_parquet('${radarCachePath}')`,
      persist
    ).catch(e => devWarn('gold_radar_profile_cache not available:', e)),
  ];

  // Load all basic dimensions in parallel
  await Promise.all(tasks);
  devLog('Essential dimensions loaded.');

  // Essential derived dimension
  await ensureTable(
    connection,
    'dim_entity',
    `SELECT 'municipality' AS entity_type, municipality_id AS entity_id, municipality_name AS entity_name FROM dim_municipality
     UNION ALL
     SELECT 'org_unit' AS entity_type, school_id AS entity_id, school_name AS entity_name FROM dim_school`,
    persist
  );
}

/**
 * Background load of non-essential dimension tables to avoid blocking the first paint.
 */
async function loadSecondaryDimensions(connection: DuckConnection, normalizedBase: string, persist = true) {
  globalSecondaryReady = false;
  devLog('Background loading secondary dimension tables...');

  const dimSchoolYearPath = escapeSqlString(joinS3Path(normalizedBase, 'dim_school_year.parquet'));

  try {
    // 1. Org unit year (used for governance filters)
    await ensureTable(
      connection,
      'dim_org_unit_year',
      `SELECT CAST(year AS INTEGER) AS year, CAST(entity_id AS VARCHAR) AS entity_id, governance_type_year FROM read_parquet('${dimSchoolYearPath}')`,
      persist
    );

    // 2. Enriched org unit (used for map details)
    await ensureTable(
      connection,
      'dim_org_unit_enriched',
      `SELECT 
         s.school_id,
         s.school_name,
         s.municipality_id,
         m.municipality_name,
         gy.governance_type_year,
         s.school_id AS id,
         s.school_name AS name,
         s.latitude,
         s.longitude
       FROM dim_school s
       LEFT JOIN dim_municipality m ON m.municipality_id = s.municipality_id
       LEFT JOIN (
         SELECT entity_id, governance_type_year
         FROM (
           SELECT 
             entity_id,
             governance_type_year,
             row_number() OVER (PARTITION BY entity_id ORDER BY governance_type_year) AS rn
           FROM dim_org_unit_year
         )
         WHERE rn = 1
       ) gy ON gy.entity_id = s.school_id`,
      persist
    );

    // Note: dim_kpi already loaded as dim_kpi_minimal in essentials phase
    // Full dim_kpi.parquet is not exported, so we skip the optional extended load

    devLog('✅ Secondary dimensions loaded.');
    globalSecondaryReady = true;
    invalidateQueryCaches('secondary dimensions ready');
  } catch (err) {
    devWarn('Failed to load secondary dimensions:', err);
  }
}


async function getTableColumns(connection: DuckConnection, tableName: string): Promise<string[]> {
  try {
    const result = await connection.query(`SELECT column_name FROM duckdb_columns() WHERE table_name = '${tableName}'`);
    const rows = result.toArray();
    return rows.map((row: Record<string, unknown>) => String(row.column_name).toLowerCase());
  } catch {
    return [];
  }
}

async function buildIndexes(connection: DuckConnection) {
  // Check what columns exist in each table to build appropriate indexes
  const orgUnitCols = await getTableColumns(connection, FACT_ORG_UNIT_TABLE);
  const muniCols = await getTableColumns(connection, FACT_MUNICIPALITY_TABLE);

  const statements: string[] = [];

  // For org_unit table: prefer municipality_id if it exists, fallback to entity_id
  if (orgUnitCols.includes('municipality_id')) {
    statements.push(`CREATE INDEX IF NOT EXISTS idx_fact_kpi_trend_org_unit_muni ON ${FACT_ORG_UNIT_TABLE} (municipality_id, kpi_id, gender, year)`);
  }

  if (orgUnitCols.includes('entity_id')) {
    // Index for specific entity lookups (schools) - crucial for IndicatorTiles and drill-downs
    statements.push(`CREATE INDEX IF NOT EXISTS idx_fact_kpi_trend_org_unit_entity_gender ON ${FACT_ORG_UNIT_TABLE} (entity_id, kpi_id, gender, year DESC)`);
  }

  // For municipality table: prefer municipality_id if it exists, fallback to entity_id
  if (muniCols.includes('municipality_id')) {
    statements.push(`CREATE INDEX IF NOT EXISTS idx_fact_kpi_trend_muni_gender ON ${FACT_MUNICIPALITY_TABLE} (municipality_id, kpi_id, gender, year)`);
  }

  if (muniCols.includes('entity_id')) {
    statements.push(`CREATE INDEX IF NOT EXISTS idx_fact_kpi_trend_muni_entity_gender ON ${FACT_MUNICIPALITY_TABLE} (entity_id, kpi_id, gender, year DESC)`);
  }

  // General index for KPI-first lookups (national averages) - adding gender here is critical
  statements.push(`CREATE INDEX IF NOT EXISTS idx_fact_kpi_trend_org_unit_kpi_gender ON ${FACT_ORG_UNIT_TABLE} (kpi_id, gender, year)`);
  statements.push(`CREATE INDEX IF NOT EXISTS idx_fact_kpi_trend_muni_kpi_gender ON ${FACT_MUNICIPALITY_TABLE} (kpi_id, gender, year)`);

  for (const statement of statements) {
    try {
      await connection.query(statement);
      devLog('Created index:', statement);
    } catch (error) {
      devWarn(`Skipping index creation: ${statement} `, error);
    }
  }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function populateFactTables(connection: DuckConnection, factBase: string, persist = true) {
  devLog('📥 Loading fact tables into OPFS from', factBase);
  devLog('📥 This should pull fresh data from S3 with gender=T included');
  await connection.query('BEGIN TRANSACTION');
  let committed = false;

  try {
    // IMPORTANT: Use glob pattern /**/*.parquet to read all partitions from Hive-partitioned directories
    const muniPath = escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_municipality.parquet/**/*.parquet'));
    devLog('📥 Reading municipality fact from:', muniPath);

    await ensureTable(
      connection,
      FACT_MUNICIPALITY_TABLE,
      `SELECT ${FACT_KPI_BASE_COLUMNS} FROM read_parquet('${muniPath}')`,
      persist
    );

    const orgPath = escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_org_unit.parquet/**/*.parquet'));
    await ensureTable(
      connection,
      FACT_ORG_UNIT_TABLE,
      `SELECT ${FACT_KPI_BASE_COLUMNS} FROM read_parquet('${orgPath}')`,
      persist
    );

    await connection.query('COMMIT');
    committed = true;

    // DEBUG: Check gender distribution after loading
    const genderDist = await connection.query(
      `SELECT gender, COUNT(*) as cnt FROM ${FACT_MUNICIPALITY_TABLE} GROUP BY gender ORDER BY gender`
    );
    devLog('📊 Gender distribution in freshly loaded fact_kpi_trend_municipality:', genderDist.toArray());

    const check2580 = await connection.query(
      `SELECT DISTINCT gender FROM ${FACT_MUNICIPALITY_TABLE} WHERE entity_id = '2580' AND kpi_id = 'N15504'`
    );
    devLog('📊 Genders for entity 2580/N15504:', check2580.toArray());

  } catch (error) {
    devError('Failed to populate fact tables:', error);
    if (String(error).includes('No modification allowed')) {
      setOpfsDisabled(true);
    }
    throw error;
  } finally {
    if (!committed) {
      try {
        await connection.query('ROLLBACK');
      } catch (rollbackError) {
        devWarn('Rollback failed:', rollbackError);
      }
    }
  }
}

async function createFactViews(connection: DuckConnection, factBase?: string) {
  // Use S3 paths as fallback if local tables don't exist yet
  const muniSource = (await tablesExist(connection, [FACT_MUNICIPALITY_TABLE]))
    ? FACT_MUNICIPALITY_TABLE
    : factBase ? `read_parquet('${escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_municipality.parquet/**/*.parquet'))}')` : null;

  const orgSource = (await tablesExist(connection, [FACT_ORG_UNIT_TABLE]))
    ? FACT_ORG_UNIT_TABLE
    : factBase ? `read_parquet('${escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_org_unit.parquet/**/*.parquet'))}')` : null;

  if (!muniSource || !orgSource) {
    devWarn('Cannot create fact views: missing sources', { muniSource, orgSource });
    return;
  }

  const muniTrend = sqlMuniFactWithTrend(muniSource);
  const orgTrend = sqlOrgFactWithTrend(orgSource);
  const muniBase = sqlMuniFactBase(muniSource);
  const orgBase = sqlOrgFactBase(orgSource);

  for (const name of ['gold_kpi_trend', 'gold_kpi_muni_year', 'gold_kpi_ou_latest', 'gold_kpi_metadata']) {
    await connection.query(`DROP VIEW IF EXISTS ${name} `);
    await connection.query(`DROP TABLE IF EXISTS ${name} `);
  }

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_trend AS
    SELECT
    'municipality' AS entity_type,
      LPAD(CAST(f.entity_id AS VARCHAR), 4, '0') AS entity_id,
        m.municipality_name AS entity_name,
          LPAD(CAST(f.entity_id AS VARCHAR), 4, '0') AS municipality_id,
            m.municipality_name AS municipality_name,
              m.county_code,
              m.county_name,
              m.skr_group_id,
              m.skr_group_name,
              m.municipality_type,
              CAST(f.kpi_id AS VARCHAR) AS kpi_id,
                k.kpi_title,
                CAST(f.year AS INTEGER) AS year,
                  f.gender,
                  f.value,
                  f.count,
                  f.status,
                  f.unit,
                  f.students,
                  f.weighted_value,
                  f.governance_type_year,
                  f.prev_year_value,
                  f.value_change,
                  f.value_change_pct
      FROM ${muniTrend} f
       LEFT JOIN dim_municipality m ON m.municipality_id = LPAD(CAST(f.entity_id AS VARCHAR), 4, '0')
       LEFT JOIN dim_kpi k ON k.kpi_id = CAST(f.kpi_id AS VARCHAR)
       UNION ALL
    SELECT
    'org_unit' AS entity_type,
      CAST(f.entity_id AS VARCHAR) AS entity_id,
        s.school_name AS entity_name,
          LPAD(CAST(s.municipality_id AS VARCHAR), 4, '0') AS municipality_id,
            m.municipality_name AS municipality_name,
              m.county_code,
              m.county_name,
              m.skr_group_id,
              m.skr_group_name,
              m.municipality_type,
              CAST(f.kpi_id AS VARCHAR) AS kpi_id,
                k.kpi_title,
                CAST(f.year AS INTEGER) AS year,
                  f.gender,
                  f.value,
                  f.count,
                  f.status,
                  f.unit,
                  f.students,
                  f.weighted_value,
                  f.governance_type_year,
                  f.prev_year_value,
                  f.value_change,
                  f.value_change_pct
      FROM ${orgTrend} f
       LEFT JOIN dim_school s ON s.school_id = CAST(f.entity_id AS VARCHAR)
       LEFT JOIN dim_municipality m ON m.municipality_id = LPAD(CAST(s.municipality_id AS VARCHAR), 4, '0')
       LEFT JOIN dim_kpi k ON k.kpi_id = CAST(f.kpi_id AS VARCHAR)`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_muni_year AS
    SELECT
    CAST(f.entity_id AS VARCHAR) AS municipality_id,
      m.municipality_name,
      CAST(f.kpi_id AS VARCHAR) AS kpi_id,
        k.kpi_title,
        CAST(f.year AS INTEGER) AS year,
          f.value AS value_t
      FROM ${muniBase} f
       LEFT JOIN dim_municipality m ON m.municipality_id = CAST(f.entity_id AS VARCHAR)
       LEFT JOIN dim_kpi k ON k.kpi_id = CAST(f.kpi_id AS VARCHAR)
       WHERE COALESCE(NULLIF(f.gender, ''), 'T') = 'T'`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_ou_latest AS
       WITH ranked AS(
            SELECT
           f.entity_id,
           f.municipality_id,
           f.kpi_id,
           f.year,
           f.gender,
           f.value,
           f.count,
           f.status,
           f.unit,
           f.students,
           f.weighted_value,
           f.governance_type_year,
            row_number() OVER(
              PARTITION BY f.entity_id, f.kpi_id
             ORDER BY f.year DESC
            ) AS rn
         FROM ${orgBase} f
          )
    SELECT
    r.entity_id,
      s.school_name AS entity_name,
        s.municipality_id,
        m.municipality_name,
        r.kpi_id,
        r.year,
        r.value,
        r.unit,
        r.students,
        r.weighted_value,
        r.governance_type_year
       FROM ranked r
       LEFT JOIN dim_school s ON s.school_id = r.entity_id
       LEFT JOIN dim_municipality m ON m.municipality_id = s.municipality_id
       WHERE rn = 1`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_muni_gender_year AS
    SELECT
    CAST(entity_id AS VARCHAR) AS municipality_id,
      CAST(kpi_id AS VARCHAR) AS kpi_id,
        CAST(year AS INTEGER) AS year,
          COALESCE(NULLIF(gender, ''), 'T') AS gender,
            value
       FROM ${muniBase} AS _muni_gender`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_national_year AS
    SELECT
    CAST(kpi_id AS VARCHAR) AS kpi_id,
      CAST(year AS INTEGER) AS year,
        COALESCE(NULLIF(gender, ''), 'T') AS gender,
          AVG(value) AS national_avg,
            SUM(value * students) / NULLIF(SUM(students), 0) AS national_weighted_avg,
              MIN(value) AS national_min,
                MAX(value) AS national_max
       FROM ${orgBase} AS _org_nat
       WHERE value IS NOT NULL
       GROUP BY kpi_id, year, gender`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_metadata AS
    --OPTIMIZATION: Use dim_kpi instead of scanning fact tables from S3 on startup
    --The flags has_ou_data and has_data are defaulted to true to keep it fast
    SELECT
    k.kpi_id,
      k.kpi_title AS title,
        NULL AS description,
          k.is_divided_by_gender,
          NULL AS operating_area,
            NULL AS perspective,
              TRUE AS has_ou_data,
                NULL AS municipality_type,
                  NULL AS publication_date,
                    TRUE AS has_data
       FROM dim_kpi k`
  );
}

// Streaming mode: creates views that read directly from S3 parquet files
// Used when OPFS is unavailable to avoid loading all data into memory
// eslint-disable-next-line @typescript-eslint/no-unused-vars
async function createFactViewsFromS3(connection: DuckConnection, factBase: string) {
  for (const name of ['gold_kpi_trend', 'gold_kpi_muni_year', 'gold_kpi_ou_latest', 'gold_kpi_metadata']) {
    await connection.query(`DROP VIEW IF EXISTS ${name} `);
    await connection.query(`DROP TABLE IF EXISTS ${name} `);
  }

  // IMPORTANT: Use glob pattern /**/*.parquet to read all partitions from Hive-partitioned directories
  const muniPath = escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_municipality.parquet/**/*.parquet'));
  const orgPath = escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_org_unit.parquet/**/*.parquet'));
  devLog('📡 S3 streaming paths (with glob):', { muniPath, orgPath });

  const muniSource = `read_parquet('${muniPath}')`;
  const orgSource = `read_parquet('${orgPath}')`;
  const muniTrend = sqlMuniFactWithTrend(muniSource);
  const orgTrend = sqlOrgFactWithTrend(orgSource);
  const muniBase = sqlMuniFactBase(muniSource);
  const orgBase = sqlOrgFactBase(orgSource);

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_trend AS
    SELECT
    'municipality' AS entity_type,
      LPAD(CAST(f.entity_id AS VARCHAR), 4, '0') AS entity_id,
        m.municipality_name AS entity_name,
          LPAD(CAST(f.entity_id AS VARCHAR), 4, '0') AS municipality_id,
            m.municipality_name AS municipality_name,
              m.county_code,
              m.county_name,
              m.skr_group_id,
              m.skr_group_name,
              m.municipality_type,
              CAST(f.kpi_id AS VARCHAR) AS kpi_id,
                k.kpi_title,
                CAST(f.year AS INTEGER) AS year,
                  f.gender,
                  f.value,
                  f.count,
                  f.status,
                  f.unit,
                  f.students,
                  f.weighted_value,
                  f.governance_type_year,
                  f.prev_year_value,
                  f.value_change,
                  f.value_change_pct
       FROM ${muniTrend} f
       LEFT JOIN dim_municipality m ON m.municipality_id = LPAD(CAST(f.entity_id AS VARCHAR), 4, '0')
       LEFT JOIN dim_kpi k ON k.kpi_id = CAST(f.kpi_id AS VARCHAR)
       UNION ALL
    SELECT
    'org_unit' AS entity_type,
      CAST(f.entity_id AS VARCHAR) AS entity_id,
        s.school_name AS entity_name,
          LPAD(CAST(s.municipality_id AS VARCHAR), 4, '0') AS municipality_id,
            m.municipality_name AS municipality_name,
              m.county_code,
              m.county_name,
              m.skr_group_id,
              m.skr_group_name,
              m.municipality_type,
              CAST(f.kpi_id AS VARCHAR) AS kpi_id,
                k.kpi_title,
                CAST(f.year AS INTEGER) AS year,
                  f.gender,
                  f.value,
                  f.count,
                  f.status,
                  f.unit,
                  f.students,
                  f.weighted_value,
                  f.governance_type_year,
                  f.prev_year_value,
                  f.value_change,
                  f.value_change_pct
       FROM ${orgTrend} f
       LEFT JOIN dim_school s ON s.school_id = CAST(f.entity_id AS VARCHAR)
       LEFT JOIN dim_municipality m ON m.municipality_id = LPAD(CAST(s.municipality_id AS VARCHAR), 4, '0')
       LEFT JOIN dim_kpi k ON k.kpi_id = CAST(f.kpi_id AS VARCHAR)`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_muni_year AS
    SELECT
    CAST(f.entity_id AS VARCHAR) AS municipality_id,
      m.municipality_name,
      CAST(f.kpi_id AS VARCHAR) AS kpi_id,
        k.kpi_title,
        CAST(f.year AS INTEGER) AS year,
          f.value AS value_t
       FROM ${muniBase} f
       LEFT JOIN dim_municipality m ON m.municipality_id = CAST(f.entity_id AS VARCHAR)
       LEFT JOIN dim_kpi k ON k.kpi_id = CAST(f.kpi_id AS VARCHAR)
       WHERE COALESCE(NULLIF(f.gender, ''), 'T') = 'T'`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_ou_latest AS
       WITH ranked AS(
            SELECT
           f.entity_id,
           f.municipality_id,
           f.kpi_id,
           f.year,
           f.gender,
           f.value,
           f.count,
           f.status,
           f.unit,
           f.students,
           f.weighted_value,
           f.governance_type_year,
            row_number() OVER(
              PARTITION BY f.entity_id, f.kpi_id
             ORDER BY f.year DESC
            ) AS rn
         FROM ${orgBase} f
          )
    SELECT
    r.entity_id,
      s.school_name AS entity_name,
        s.municipality_id,
        m.municipality_name,
        r.kpi_id,
        r.year,
        r.value,
        r.unit,
        r.students,
        r.weighted_value,
        r.governance_type_year
       FROM ranked r
       LEFT JOIN dim_school s ON s.school_id = r.entity_id
       LEFT JOIN dim_municipality m ON m.municipality_id = s.municipality_id
       WHERE rn = 1`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_muni_gender_year AS
    SELECT
    CAST(entity_id AS VARCHAR) AS municipality_id,
      CAST(kpi_id AS VARCHAR) AS kpi_id,
        CAST(year AS INTEGER) AS year,
          COALESCE(NULLIF(gender, ''), 'T') AS gender,
            value
       FROM ${muniBase} AS _muni_gender`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_national_year AS
    SELECT
    CAST(kpi_id AS VARCHAR) AS kpi_id,
      CAST(year AS INTEGER) AS year,
        COALESCE(NULLIF(gender, ''), 'T') AS gender,
          AVG(value) AS national_avg,
            SUM(value * students) / NULLIF(SUM(students), 0) AS national_weighted_avg,
              MIN(value) AS national_min,
                MAX(value) AS national_max
       FROM ${orgBase} AS _org_nat
       WHERE value IS NOT NULL
       GROUP BY kpi_id, year, gender`
  );

  await connection.query(
    `CREATE OR REPLACE VIEW gold_kpi_metadata AS
    --OPTIMIZATION: Use dim_kpi directly instead of scanning fact tables from S3 on startup
    --The flags has_ou_data and has_data are defaulted to true to keep it fast
    SELECT
    k.kpi_id,
      k.kpi_title AS title,
        NULL AS description,
          k.is_divided_by_gender,
          NULL AS operating_area,
            NULL AS perspective,
              TRUE AS has_ou_data,
                NULL AS municipality_type,
                  NULL AS publication_date,
                    TRUE AS has_data
       FROM dim_kpi k`
  );
}

async function markCacheRefreshed(connection: DuckConnection) {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.setItem(CACHE_TIMESTAMP_KEY, Date.now().toString());
      window.localStorage.setItem(CACHE_SCHEMA_VERSION_KEY, CACHE_SCHEMA_VERSION);
      devLog('Cache timestamp and schema version stored in localStorage.');
    }
  } catch (error) {
    devWarn('Failed to persist cache timestamp to localStorage.', error);
  }
}

async function tablesExist(connection: DuckConnection, tableNames: string[]) {
  if (tableNames.length === 0) return true;
  const sanitizedNames = tableNames.map((name) => `'${escapeSqlString(name.toLowerCase())}'`).join(', ');
  const query = `
    SELECT lower(table_name) AS table_name
    FROM duckdb_tables()
    WHERE lower(table_name) IN(${sanitizedNames})
  `;
  try {
    const result = await connection.query(query);
    const rows = result.toArray();
    return rows.length === tableNames.length;
  } catch (error) {
    devWarn('Failed to check table existence:', error);
    return false;
  }
}

async function shouldRefreshCache(connection: DuckConnection) {
  devLog('🔄 shouldRefreshCache called, current CACHE_SCHEMA_VERSION:', CACHE_SCHEMA_VERSION);

  if (isOpfsDisabled()) {
    devLog('🔄 OPFS disabled, skipping cache');
    return false;
  }

  // Check schema version first - if it changed, we need to refresh regardless of other checks
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const cachedVersion = window.localStorage.getItem(CACHE_SCHEMA_VERSION_KEY);
      devLog('🔄 Cached schema version:', cachedVersion, '| Current:', CACHE_SCHEMA_VERSION);
      if (cachedVersion !== CACHE_SCHEMA_VERSION) {
        devLog(`🚨 Cache refresh required: schema version changed(cached: ${cachedVersion}, current: ${CACHE_SCHEMA_VERSION})`);
        return true;
      }
    }
  } catch (error) {
    devWarn('Schema version check failed; refreshing cache.', error);
    return true;
  }

  // Note: dim_municipality_geo is optional (may not exist in older exports)
  const requiredTables = [
    'dim_year',
    'dim_municipality',
    'dim_school',
    'dim_org_unit_year',
    'dim_kpi',
    'dim_entity',
    'dim_org_unit_enriched',
    'dim_municipality_geo',
    'gold_radar_profile_cache',
    FACT_MUNICIPALITY_TABLE,
    FACT_ORG_UNIT_TABLE,
  ];

  const haveTables = await tablesExist(connection, requiredTables);
  if (!haveTables) {
    devLog('Cache refresh required: one or more tables missing.');
    return true;
  }

  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const raw = window.localStorage.getItem(CACHE_TIMESTAMP_KEY);
      if (!raw) {
        devLog('Cache refresh required: timestamp missing in localStorage.');
        return true;
      }

      const refreshedMs = Number.parseInt(raw, 10);
      if (Number.isNaN(refreshedMs)) {
        devLog('Cache refresh required: invalid timestamp in localStorage.');
        return true;
      }

      const ageMs = Date.now() - refreshedMs;
      if (ageMs >= CACHE_TTL_MS) {
        devLog('Cache refresh required: data older than 24 hours.');
        return true;
      }

      devLog('OPFS cache is fresh; age (minutes):', Math.round(ageMs / 60000));
      return false;
    }

    devLog('Cache refresh required: localStorage unavailable.');
    return true;
  } catch (error) {
    devWarn('Cache timestamp lookup failed; refreshing cache.', error);
    return true;
  }
}

export const useSqlRooms = () => {
  const [ready, setReady] = useState(globalReady);
  const [secondaryReady, setSecondaryReady] = useState(globalSecondaryReady);
  const [error, setError] = useState<string | null>(globalError);
  const [dataVersion, setDataVersion] = useState(globalDataVersion);

  // Synchronize local hook state with global variables whenever they change
  useEffect(() => {
    const listener = (v: number) => {
      setDataVersion(v);
      setReady(globalReady);
      setSecondaryReady(globalSecondaryReady);
      setError(globalError);
    };
    versionListeners.add(listener);
    // Initial sync
    listener(globalDataVersion);
    return () => { versionListeners.delete(listener); };
  }, []);

  const initialize = useCallback(async () => {
    if (globalReady) {
      setSecondaryReady(globalSecondaryReady);
      return;
    }
    if (initializationPromise) {
      await initializationPromise;
      setReady(globalReady);
      setSecondaryReady(globalSecondaryReady);
      setError(globalError);
      return;
    }

    initializationPromise = (async () => {
      try {
        devLog('Starting DuckDB initialization for S3 → OPFS cache...');

        if (!db) {
          devLog('Initializing DuckDB WASM client...');

          try {
            // Use CDN for WASM files (too large for Cloudflare Pages 25MB limit)
            // Keep worker JS files local (small enough to bundle)
            const CDN_BASE = 'https://cdn.jsdelivr.net/npm/@duckdb/duckdb-wasm@1.31.0/dist';
            const MANUAL_BUNDLES: duckdb.DuckDBBundles = {
              mvp: {
                mainModule: `${CDN_BASE}/duckdb-mvp.wasm`,
                mainWorker: '/workers/duckdb-browser-mvp.worker.js',
              },
              eh: {
                mainModule: `${CDN_BASE}/duckdb-eh.wasm`,
                mainWorker: '/workers/duckdb-browser-eh.worker.js',
              },
            };

            const bundle = await duckdb.selectBundle(MANUAL_BUNDLES);
            const worker = new Worker(bundle.mainWorker!);
            const logger = new ConditionalLogger();
            db = new duckdb.AsyncDuckDB(logger, worker);
            await db.instantiate(bundle.mainModule);
            devLog('DuckDB client ready (local bundles).');
          } catch (localBundleError) {
            devWarn('Local bundles failed, falling back to jsdelivr:', localBundleError);
            const bundle = await duckdb.selectBundle(duckdb.getJsDelivrBundles());
            const worker = new Worker(bundle.mainWorker!);
            const logger = new ConditionalLogger();
            db = new duckdb.AsyncDuckDB(logger, worker);
            await db.instantiate(bundle.mainModule);
            devLog('DuckDB client ready (jsdelivr bundles).');
          }

          const isMobile = isMobileBrowser();
          const opfsPreviouslyDisabled = isOpfsDisabled() || !supportsFileSystemAccess || isMobile;
          let opfsHandleRegistered = false;

          if (!opfsPreviouslyDisabled) {
            opfsHandleRegistered = await registerOpfsDatabase(db);
          }
          const opfsWritable = !opfsPreviouslyDisabled && opfsHandleRegistered;

          await db.open({
            path: opfsWritable ? OPFS_DB_URI : ':memory:',
            accessMode: opfsWritable
              ? duckdb.DuckDBAccessMode.READ_WRITE
              : duckdb.DuckDBAccessMode.AUTOMATIC,
            opfs: opfsWritable
              ? { fileHandling: opfsHandleRegistered ? 'manual' : 'auto' }
              : undefined,
          });

          if (!opfsWritable && !opfsPreviouslyDisabled) {
            try {
              await db.registerOPFSFileName(OPFS_DB_URI);
            } catch (registerErr) {
              devWarn('registerOPFSFileName failed;', registerErr);
            }
          }

          conn = await db.connect();
          await conn.query('PRAGMA enable_progress_bar=false;');

          // Ensure search path includes the OPFS database name if it was attached with a name
          // This may fail if kolada-cache schema doesn't exist - that's OK, we'll use main
          try {
            await conn.query("PRAGMA search_path='main,kolada-cache'");
          } catch {
            // Expected in environments without the kolada-cache schema - silently ignore
          }

          // Memory optimizations for constrained environments
          try {
            // Disable insertion order preservation - significant memory savings
            await conn.query(`SET preserve_insertion_order=false`);
            // Limit threads on mobile to reduce memory pressure
            if (isMobile) {
              await conn.query(`SET threads=1`);
              // Mobile browsers (especially iOS Safari) have tight WASM memory ceilings.
              // Increasing to 512MB to handle larger municipalities like Uppsala.
              await conn.query(`PRAGMA memory_limit='512MB'`);
            } else {
              await conn.query(`PRAGMA memory_limit='2GB'`);
            }
          } catch (optErr) {
            devWarn('Could not set DuckDB optimizations:', optErr);
          }

          // 1. Parallelize extension and S3 setup
          const awsAccessKeyId = process.env.REACT_APP_AWS_ACCESS_KEY_ID;
          const awsSecretAccessKey = process.env.REACT_APP_AWS_SECRET_ACCESS_KEY;
          const awsRegion = process.env.REACT_APP_AWS_REGION || 'eu-north-1';
          const wasmBucketEnv = process.env.REACT_APP_WASM_EXPORT_BUCKET || process.env.REACT_APP_S3_BUCKET;
          const wasmPrefixEnv = process.env.REACT_APP_WASM_EXPORT_PREFIX || DEFAULT_EXPORT_PREFIX;

          const s3Bucket = normalizeBucket(wasmBucketEnv);
          const exportRoot = joinS3Path(s3Bucket, wasmPrefixEnv);
          const normalizedBase = joinS3Path(exportRoot, 'normalized');
          const factBase = joinS3Path(exportRoot, 'fact');
          lastNormalizedS3Base = normalizedBase;

          // Essential extension for S3 access
          await conn.query('INSTALL httpfs');
          await conn.query('LOAD httpfs');

          await conn.query(`SET s3_region='${awsRegion}'`);
          await conn.query(`SET s3_endpoint='s3.${awsRegion}.amazonaws.com'`);
          await conn.query("SET s3_use_ssl=true");
          await conn.query("SET s3_url_style='vhost'");

          if (awsAccessKeyId && awsSecretAccessKey) {
            await conn.query(`SET s3_access_key_id='${escapeSqlString(awsAccessKeyId)}'`);
            await conn.query(`SET s3_secret_access_key='${escapeSqlString(awsSecretAccessKey)}'`);
          }

          // Spatial is heavy; skip on mobile to reduce peak memory (boundaries use read_parquet, not ST_*).
          if (!isMobile) {
            void (async () => {
              try {
                await conn!.query('INSTALL spatial');
                await conn!.query('LOAD spatial');
                devLog('✅ DuckDB Spatial extension loaded in background.');
              } catch (spatialErr) {
                devWarn('Could not load DuckDB Spatial extension:', spatialErr);
              }
            })();
          }

          // 2. Load dimension tables and ESSENTIAL fact views in parallel
          if (opfsWritable) {
            if (isDevelopment) console.time('shouldRefreshCache');
            const refreshCache = await shouldRefreshCache(conn);
            if (isDevelopment) console.timeEnd('shouldRefreshCache');

            if (refreshCache) {
              devLog('Refreshing OPFS cache (Essentials First)...');
              if (isDevelopment) console.time('essentials_load');
              // First, load essential dimensions in parallel (very small)
              await loadDimensionTables(conn, normalizedBase, true);

              // Materialize org-unit facts before setReady (same connection → sequential queries).
              // Previously org loaded in background while views used S3 — Kommun then hit slow remote Parquet.
              const muniPath = escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_municipality.parquet/**/*.parquet'));
              const orgPath = escapeSqlString(joinS3Path(factBase, 'fact_kpi_trend_org_unit.parquet/**/*.parquet'));
              await ensureTable(conn, FACT_MUNICIPALITY_TABLE, `SELECT ${FACT_KPI_BASE_COLUMNS} FROM read_parquet('${muniPath}')`, true);
              await ensureTable(conn, FACT_ORG_UNIT_TABLE, `SELECT ${FACT_KPI_BASE_COLUMNS} FROM read_parquet('${orgPath}')`, true);
              if (isDevelopment) console.timeEnd('essentials_load');

              // Views use local fact tables — school-level queries avoid cold S3 scans on first paint.
              await createFactViews(conn, factBase);
              invalidateQueryCaches('created initial views (essentials ready)');
              globalReady = true;
              setReady(true); // Signal ready to the UI early!
              markDuckdbReadyForProfiling();

              // Background: secondary dimensions + indexes (facts already materialized above).
              void (async () => {
                try {
                  if (isDevelopment) console.time('background_load');
                  await loadSecondaryDimensions(conn!, normalizedBase, true);
                  await buildIndexes(conn!);
                  await markCacheRefreshed(conn!);
                  if (isDevelopment) console.timeEnd('background_load');
                  devLog('✅ Full background cache load complete.');
                } catch (e) {
                  devWarn('Background load failed:', e);
                }
              })();
            } else {
              devLog('Reusing OPFS cache.');
              await createFactViews(conn);
              invalidateQueryCaches('reused OPFS cache; recreated views');
              globalReady = true;
              setReady(true);
              markDuckdbReadyForProfiling();

              // Background: Load secondary dimensions to ensure tables exist in THIS connection
              void loadSecondaryDimensions(conn, normalizedBase, true);
            }
          } else {
            setOpfsDisabled(true);
            // Mobile / no OPFS: dimensions + S3-backed views only — no CTAS of org facts (avoids iPhone OOM).
            devLog('📡 Streaming mode - Essentials First...');
            if (isDevelopment) console.time('streaming_essentials');

            // Load dimensions only; keep municipality facts on S3 (Hive year partitions) to avoid mobile OOM
            await loadDimensionTables(conn, normalizedBase, false);

            // Create views: muni + org read from S3 via read_parquet with partition pruning per query
            await createFactViews(conn, factBase);
            invalidateQueryCaches('streaming mode; created views');
            if (isDevelopment) console.timeEnd('streaming_essentials');

            globalReady = true;
            setReady(true);
            markDuckdbReadyForProfiling();

            // Secondary dims only (still no materialized org facts on streaming).
            void loadSecondaryDimensions(conn, normalizedBase, false);
          }

          // Dev-only sanity checks (helps diagnose missing Riket line)
          if (process.env.NODE_ENV !== 'production' && globalReady) {
            void (async () => {
              try {
                const counts = await conn!.query(`
                  SELECT
                    (SELECT COUNT(*) FROM gold_kpi_muni_year WHERE kpi_id = 'N15504') AS muni_merit_rows,
                    (SELECT COUNT(*) FROM gold_kpi_muni_year WHERE kpi_id = 'U15416') AS muni_salsa_rows
                `);
                devLog('🧪 gold_kpi_muni_year sanity:', counts.toArray());
              } catch (e) {
                // Silently fail sanity checks
              }
            })();
          }
        }

        globalReady = true;
        globalError = null;
        devLog('Local data environment is ready for querying.');
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
        devError('Fatal initialization error:', err);
        globalError = errorMessage;
        globalReady = false;
      } finally {
        initializationPromise = null;
      }
    })();

    await initializationPromise;
    setReady(globalReady);
    setSecondaryReady(globalSecondaryReady);
    setError(globalError);
  }, []);

  const query = useCallback(async <T extends Record<string, unknown>[]>(
    sql: string,
    params: Record<string, unknown> = {}
  ): Promise<T> => {
    if (!globalReady || !conn) {
      if (globalError) {
        throw new Error(`Database not ready due to initialization error: ${globalError}`);
      }
      throw new Error('Database is not ready. Please wait for initialization.');
    }

    let boundSql = sql;

    for (const [key, value] of Object.entries(params)) {
      let paramValue: string;
      if (value === null || value === undefined) {
        paramValue = 'NULL';
      } else if (typeof value === 'string') {
        paramValue = `'${value.replace(/'/g, "''")}'`;
      } else if (typeof value === 'number') {
        paramValue = String(value);
      } else if (typeof value === 'boolean') {
        paramValue = value ? 'TRUE' : 'FALSE';
      } else {
        paramValue = `'${String(value).replace(/'/g, "''")}'`;
      }

      const regex = new RegExp(`:${key}\\b`, 'g');
      boundSql = boundSql.replace(regex, paramValue);
    }

    // Check cache
    if (queryCache.has(boundSql)) {
      return queryCache.get(boundSql);
    }

    // Deduplicate concurrent requests for the same SQL
    if (pendingQueries.has(boundSql)) {
      return pendingQueries.get(boundSql);
    }

    const queryPromise = (async () => {
      const startTime = isDevelopment ? performance.now() : 0;

      if (isDevelopment) {
        console.log('🔍 [SQL Query]:', boundSql);
      }

      try {
        const result = await conn!.query(boundSql);
        const rows = result.toArray() as T;
        const sanitized = rows.map(sanitizeRow) as T;

        if (isDevelopment) {
          const duration = performance.now() - startTime;
          console.log(`✅ [SQL Result]: ${sanitized.length} rows in ${duration.toFixed(2)}ms`);
        }

        // Cache the result
        addToQueryCache(boundSql, sanitized);

        return sanitized;
      } catch (error) {
        if (isDevelopment) {
          const duration = performance.now() - startTime;
          console.error(`❌ [SQL Error] after ${duration.toFixed(2)}ms:`, error);
        }
        throw error;
      } finally {
        pendingQueries.delete(boundSql);
      }
    })();

    pendingQueries.set(boundSql, queryPromise);
    return queryPromise;
  }, []);

  useEffect(() => {
    void initialize();
    // Intentionally no dispose on unmount: async teardown races React StrictMode remount
    // (a second mount can briefly see globalReady while the previous dispose terminates wasm).
    // Skipping dispose still yields a large iPhone win because `/` never mounts DuckDB.
  }, [initialize]);

  const getGlobalMunicipalities = useCallback(() => globalMunicipalities, []);
  const setGlobalMunicipalities = useCallback((m: any[]) => { globalMunicipalities = m; }, []);

  const ensureMunicipalityBoundaries = useCallback(async () => {
    await ensureMunicipalityBoundariesLoaded();
  }, []);

  const getCached = useCallback((sql: string, params: Record<string, unknown> = {}) => {
    let boundSql = sql;
    for (const [key, value] of Object.entries(params)) {
      let paramValue: string;
      if (value === null || value === undefined) {
        paramValue = 'NULL';
      } else if (typeof value === 'string') {
        paramValue = `'${value.replace(/'/g, "''")}'`;
      } else if (typeof value === 'number') {
        paramValue = String(value);
      } else if (typeof value === 'boolean') {
        paramValue = value ? 'TRUE' : 'FALSE';
      } else {
        paramValue = `'${String(value).replace(/'/g, "''")}'`;
      }
      const regex = new RegExp(`:${key}\\b`, 'g');
      boundSql = boundSql.replace(regex, paramValue);
    }
    const cached = queryCache.get(boundSql);
    return cached !== undefined ? cached : undefined;
  }, []);

  const reinitialize = useCallback(async () => {
    devLog('🔄 Reinitializing DuckDB due to catalog error or missing table...');

    const pending = initializationPromise;
    if (pending) {
      try {
        await pending;
      } catch {
        // ignore
      }
    }
    await disposeSqlRoomsEngine('reinitialize');
    invalidateQueryCaches('reinitialization');

    setReady(false);
    setSecondaryReady(false);
    setError(null);

    await initialize();
  }, [initialize]);

  return {
    ready,
    secondaryReady,
    error,
    query,
    dataVersion,
    // Add access to global municipalities cache
    getGlobalMunicipalities,
    setGlobalMunicipalities,
    // Synchronous access to cached results for instant rendering
    getCached,
    // Self-healing: reinitialize if database is corrupted or incomplete
    reinitialize,
    /** Mobile: load boundary GeoJSON from S3 before queries that need choropleth polygons */
    ensureMunicipalityBoundaries
  };
};

/**
 * Force clear the OPFS cache. Call this from browser console if schema changes aren't being picked up.
 * Usage: import { forceClearCache } from './services/useSqlRooms'; forceClearCache();
 * Or in console: window.forceClearKoladaCache()
 */
export const forceClearCache = async () => {
  try {
    resetMunicipalityBoundariesState();
    // Clear localStorage cache metadata
    if (typeof window !== 'undefined' && window.localStorage) {
      window.localStorage.removeItem(CACHE_TIMESTAMP_KEY);
      window.localStorage.removeItem(CACHE_SCHEMA_VERSION_KEY);
      window.localStorage.removeItem(CACHE_OPFS_DISABLED_KEY);
      devLog('Cleared localStorage cache metadata.');
    }

    // Try to delete the OPFS database file
    if (supportsFileSystemAccess) {
      try {
        const root = await (navigator.storage as any).getDirectory();
        const fileName = OPFS_DB_URI.replace('opfs://', '');
        await root.removeEntry(fileName);
        devLog('Deleted OPFS database file:', fileName);
      } catch (e) {
        devWarn('Could not delete OPFS file (may not exist):', e);
      }
    }

    devLog('Cache cleared. Reload the page to fetch fresh data from S3.');
    return true;
  } catch (error) {
    devError('Failed to clear cache:', error);
    return false;
  }
};

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as any).forceClearKoladaCache = forceClearCache;
  // Dev-only: quick introspection of what the browser DB actually contains.
  (window as any).koladaSql = debugSql;
}
